import { useState, type FormEvent } from "react";

const SUBMIT_ADDR = "https://readdy.ai/api/form/dah8o0bp14a1h3mr6fe0";

interface FormResponse {
  code?: string;
  message?: string;
  meta?: { message?: string; detail?: string };
}

export default function SubscribeForm() {
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [feedback, setFeedback] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const payload = new FormData(form);

    const guard = String(payload.get("website_alt") ?? "").trim();
    if (guard) {
      setStatus("success");
      setFeedback("订阅成功，我们会在每周更新时给你发送一封汇总邮件。");
      form.reset();
      return;
    }

    const body = new URLSearchParams();
    payload.forEach((value, key) => {
      if (key === "website_alt") return;
      if (typeof value === "string" && value.trim() !== "") {
        body.append(key, value);
      }
    });

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });

      const responseText = await response.text();
      let parsed: FormResponse | null = null;
      try {
        parsed = responseText ? (JSON.parse(responseText) as FormResponse) : null;
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");

      if (!response.ok || parsed?.code !== "OK" || isSpam) {
        setStatus("error");
        setFeedback(serverMsg || "提交失败，请检查内容后重试。");
        return;
      }

      setStatus("success");
      setFeedback(
        parsed?.meta?.message || "订阅成功，我们会在每周更新时给你发送一封汇总邮件。",
      );
      form.reset();
    } catch {
      setStatus("error");
      setFeedback("网络异常，提交未完成，请稍后重试。");
    }
  };

  return (
    <form
      data-readdy-form
      onSubmit={handleSubmit}
      className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6"
    >
      <h3 className="font-heading text-lg font-bold text-foreground-950">订阅每周爆料汇总</h3>
      <p className="mt-2 text-sm leading-relaxed text-foreground-600">
        每周挑出值得看的短剧、最新的拍摄花絮与幕后制作故事，一封邮件发给你。
      </p>

      <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <label className="flex flex-col gap-1.5">
          <span className="text-xs text-foreground-600">称呼</span>
          <input
            name="nickname"
            type="text"
            required
            placeholder="怎么称呼你"
            className="rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
          />
        </label>
        <label className="flex flex-col gap-1.5">
          <span className="text-xs text-foreground-600">邮箱</span>
          <input
            name="email"
            type="email"
            required
            placeholder="you@example.com"
            className="rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
          />
        </label>
      </div>

      <label className="mt-4 flex flex-col gap-1.5">
        <span className="text-xs text-foreground-600">最想关注的栏目</span>
        <select
          name="interest"
          defaultValue="拍摄花絮"
          className="rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
        >
          <option value="拍摄花絮">拍摄花絮</option>
          <option value="幕后制作">幕后制作</option>
          <option value="口碑评分">口碑评分</option>
          <option value="全部栏目">全部栏目</option>
        </select>
      </label>

      <label className="mt-4 flex flex-col gap-1.5">
        <span className="text-xs text-foreground-600">想让我们跟进的内容（选填）</span>
        <textarea
          name="message"
          rows={4}
          maxLength={500}
          placeholder="例如：想了解某部短剧的取景地，或希望补充哪位演员的资料。"
          className="resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
        />
      </label>

      <input
        type="text"
        name="website_alt"
        className="subscribe-guard-field"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
      />

      <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center">
        <button
          type="submit"
          className="whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
        >
          提交订阅
        </button>
        <p className="text-xs leading-relaxed text-foreground-500">
          我们只用它发送内容汇总，不会用于其他用途。
        </p>
      </div>

      {status !== "idle" && (
        <p
          role="status"
          className={`mt-4 rounded-md px-3 py-2 text-sm ${
            status === "success"
              ? "bg-accent-100 text-accent-900"
              : "bg-primary-100 text-primary-800"
          }`}
        >
          {feedback}
        </p>
      )}
    </form>
  );
}